import type { ComponentProps, FunctionComponent } from 'react'
import React, { useState } from 'react'

import classNames from 'classnames'

import { Button } from '../Button'
import { ButtonLink } from '../ButtonLink'
import { SolidPadlockIcon } from '../Icons/Miscellaneous/SolidPadlock'
import { Link } from '../Link'
import { SignUp } from '../Modal/SignUp'
import Image from '../Modal/SignUp/fixtures/modal-cover.jpg'
import type { PictureProps } from '../Picture'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import {
  presetButtonText,
  presetCaption,
  presetHref,
  presetTestCaption,
} from './SignupButton.mocks'
import styles from './SignupButton.module.scss'

export const defaultImageProps = {
  src: Image,
  alt: 'Subscribe to Which?',
}

export const SignupButton: FunctionComponent<Props> = ({
  buttonDatalayerTrackingId,
  captionDatalayerTrackingId,
  isPriceTest,
  className,
  button,
  buttonLink,
  caption,
  hidePriceInfo = false,
  withModal = false,
  withPadlock = false,
  withLogin = false,
  modalImageProps = defaultImageProps,
  ...rest
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  return withModal ? (
    <div className={className} data-testid="button-wrapper">
      <Button
        type="button"
        className={styles.button}
        data-which-id={buttonDatalayerTrackingId}
        onClick={() => {
          setModalOpen(true)
        }}
        {...rest}
      >
        <SolidPadlockIcon
          data-testid="padlock-icon"
          width={15}
          height={15}
          viewBox="0 0 15 15"
        />
        {button?.text || presetButtonText}
      </Button>
      <TypographyV2
        textStyle="sb-text-interface-body-x-small-regular"
        className={styles.caption}
        data-which-id={captionDatalayerTrackingId}
      >
        {caption || (isPriceTest ? presetTestCaption : presetCaption)}
      </TypographyV2>

      {modalOpen && (
        <SignUp
          className={styles.modal}
          image={modalImageProps}
          overline="Recommended"
          title="Full access"
          closeModal={() => setModalOpen(false)}
          modalLinkDatalayerTrackingId={buttonDatalayerTrackingId}
          data-testid="sign-up-modal"
        />
      )}
    </div>
  ) : (
    <div className={className} data-testid="button-wrapper">
      <div className={withLogin ? styles.displayLogin : ''}>
        <ButtonLink
          href={buttonLink?.href || presetHref}
          className={classNames(styles.button, {
            [styles.buttonWithLogin]: withLogin,
          })}
          data-which-id={buttonDatalayerTrackingId}
          {...rest}
        >
          {withPadlock && (
            <SolidPadlockIcon
              data-testid="padlock-icon"
              width={15}
              height={15}
              viewBox="0 0 15 15"
            />
          )}
          {buttonLink?.text || presetButtonText}
        </ButtonLink>
        {withLogin && (
          <TypographyV2 textStyle="sb-text-body-default-regular" tag="span">
            or{' '}
            <Link
              href="https://www.which.co.uk/login"
              data-testid="login-link"
              data-which-id="seatbelt-link-login"
            >
              Log in
            </Link>
          </TypographyV2>
        )}
      </div>
      {!hidePriceInfo && (
        <TypographyV2
          textStyle="sb-text-interface-body-x-small-regular"
          className={styles.caption}
          data-which-id={captionDatalayerTrackingId}
        >
          {caption || (isPriceTest ? presetTestCaption : presetCaption)}
        </TypographyV2>
      )}
    </div>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   *  Optional dataLayer tracking attribute to be rendered on the button link
   */
  buttonDatalayerTrackingId?: string
  buttonLink?: {
    text?: string
    href?: string
  }
  button?: {
    text?: string
  }
  /**
   *  Optional dataLayer tracking attribute to be rendered to the button caption text.
   */
  captionDatalayerTrackingId?: string
  caption?: string
  /**
   * Optinal class name for additional styling.
   */
  className?: string
  /**
   *  Optional property to render the price test caption.
   */
  isPriceTest?: boolean
  /**
   *  Optional property to hide the price info.
   */
  hidePriceInfo?: boolean
  /**
   *  Optional property to render the signup modal.
   */
  withModal?: boolean
  /**
   *  Optional property to render a padlock in the signup button.
   */
  withPadlock?: boolean
  /**
   *  Optional property to render a log in link next to the button.
   */
  withLogin?: boolean
  /**
   *  Optional property to render the Background image in the signup model.
   */
  modalImageProps?: PictureProps
  [key: `data-${string}`]: string | undefined
  /**
   *  Optional property to specify the button type.
   */
  type?: 'button' | 'submit' | 'reset'
} & ComponentProps<typeof Button> &
  ComponentProps<typeof ButtonLink>
